/**
 * InvoiceStatusBadge Component
 *
 * Displays a coloured badge indicating the status of an invoice.
 * Supports all invoice statuses with distinct colour coding for quick recognition.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import { Badge } from '@/components/ui/badge'

/**
 * Mapping of invoice statuses to display labels
 * Note: 'sent' is collapsed into 'Unpaid' for simplicity
 */
const STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Unpaid',
  unpaid: 'Unpaid',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  written_off: 'Written Off',
  voided: 'Voided'
}

/**
 * Mapping of invoice statuses to badge variants
 * Colours:
 * - draft: Grey (not yet ready)
 * - sent: Amber (collapsed into unpaid)
 * - unpaid: Amber (attention needed)
 * - partially_paid: Purple (in progress)
 * - paid: Green (complete)
 * - written_off: Slate (resolved but not paid)
 * - voided: Red (cancelled)
 */
const STATUS_VARIANTS = {
  draft: 'invoice-draft',
  sent: 'invoice-unpaid',
  unpaid: 'invoice-unpaid',
  partially_paid: 'invoice-partially-paid',
  paid: 'invoice-paid',
  written_off: 'invoice-written-off',
  voided: 'invoice-voided'
}

/**
 * GoCardless payment status → single badge config.
 * When a GC payment status exists, it replaces the invoice status badge
 * so the user sees the GC lifecycle state directly.
 */
const GC_PAYMENT_CONFIG = {
  pending_customer_approval: { variant: 'gc-pending', label: 'Pending Approval' },
  pending_submission: { variant: 'gc-pending', label: 'Pending Submission' },
  submitted: { variant: 'gc-pending', label: 'Submitted' },
  confirmed: { variant: 'gc-success', label: 'Confirmed' },
  paid_out: { variant: 'gc-success', label: 'Paid Out' },
  failed: { variant: 'gc-failed', label: 'Failed' },
  customer_approval_denied: { variant: 'gc-failed', label: 'Denied' },
  charged_back: { variant: 'gc-failed', label: 'Charged Back' },
  late_failure_settled: { variant: 'gc-failed', label: 'Late Failure' },
  cancelled: { variant: 'gc-cancelled', label: 'Cancelled' },
}

/**
 * InvoiceStatusBadge displays a single combined badge.
 * For GC invoices, the GC payment lifecycle status is shown directly.
 * For non-GC invoices, the invoice payment status is shown.
 *
 * @param {Object} props - Component props
 * @param {string} props.status - Invoice status (draft, sent, unpaid, partially_paid, paid, written_off, voided)
 * @param {string} [props.gcPaymentStatus] - GoCardless payment status (optional)
 * @param {string} [props.className] - Additional CSS classes
 */
export function InvoiceStatusBadge({ status, gcPaymentStatus, className }) {
  const gcConfig = gcPaymentStatus ? GC_PAYMENT_CONFIG[gcPaymentStatus] : null

  if (gcConfig) {
    return (
      <Badge variant={gcConfig.variant} className={className}>
        {gcConfig.label}
      </Badge>
    )
  }

  const label = STATUS_LABELS[status] || status
  const variant = STATUS_VARIANTS[status] || 'default'

  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  )
}

export default InvoiceStatusBadge
